import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';

const lineIsH2 = (line: string) => /^\d+\.\s+/.test(line);
const lineIsH3 = (line: string) => /^\d+\.\d+\s+/.test(line);

export const PrivacyPolicy: React.FC = () => {
  const [text, setText] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    fetch('/privacy')
      .then(async (res) => {
        if (!res.ok) throw new Error('Failed to load privacy policy');
        const t = await res.text();
        setText(t);
      })
      .catch(() => setError('Failed to load privacy policy'))
      .finally(() => setLoading(false));
  }, []);

  const renderRich = () => {
    const lines = text.split(/\r?\n/).filter((l) => l.trim().length > 0);
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < lines.length) {
      const line = lines[i];
      if (i === 0) {
        nodes.push(
          <h1 key={`h1-${i}`} className="text-3xl md:text-4xl font-bold text-gray-900 mb-2">
            {line}
          </h1>
        );
        i++;
        continue;
      }
      if (/^Last updated:/i.test(line)) {
        nodes.push(
          <p key={`updated-${i}`} className="text-sm text-gray-500 mb-6">
            {line}
          </p>
        );
        i++;
        continue;
      }
      if (lineIsH3(line)) {
        nodes.push(
          <h3 key={`h3-${i}`} className="text-xl font-semibold text-gray-900 mt-6 mb-2">
            {line}
          </h3>
        );
        i++;
        continue;
      }
      if (lineIsH2(line)) {
        nodes.push(
          <h2 key={`h2-${i}`} className="text-2xl font-semibold text-gray-900 mt-8 mb-3">
            {line}
          </h2>
        );
        i++;
        continue;
      }
      const colonIdx = line.indexOf(':');
      if (colonIdx > 0 && colonIdx < line.length - 1 && /[A-Za-z\)\]]/.test(line[colonIdx - 1])) {
        const label = line.substring(0, colonIdx);
        const rest = line.substring(colonIdx + 1).trim();
        nodes.push(
          <p key={`p-${i}`} className="text-gray-700 leading-7 mb-3">
            <span className="font-semibold text-gray-900">{label}:</span> {rest}
          </p>
        );
        i++;
        continue;
      }
      nodes.push(
        <p key={`p-${i}`} className="text-gray-700 leading-7 mb-3">
          {line}
        </p>
      );
      i++;
    }
    return nodes;
  };

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Privacy Policy – App Icon Generate</title>
        <meta name="description" content="Privacy Policy of App Icon Generate" />
      </Helmet>
      <div className="max-w-3xl mx-auto px-6 py-12">
        {loading && <div className="animate-pulse h-6 bg-gray-200 w-1/3 mb-4"></div>}
        {error && <div className="text-red-600 mb-4">{error}</div>}
        {!loading && !error && (
          <article className="prose prose-gray max-w-none">
            {renderRich()}
          </article>
        )}
      </div>
    </div>
  );
};

export default PrivacyPolicy;